import type { AdvancedScores, MicroProfile } from "@/types/advancedVocational"

export const microProfiles: MicroProfile[] = [
  {
    id: "cuidador-empatico",
    name: "O Cuidador Empático",
    description: "Você possui uma capacidade natural de acolher, compreender e apoiar pessoas em seus processos de desenvolvimento e cura.",
    characteristics: "exercer empatia, criar conexões humanas profundas e promover bem-estar",
    environment: "cuidado direto com pessoas, escuta ativa e processos de transformação pessoal",
    careers: [
      "Psicologia",
      "Fisioterapia",
      "Enfermagem",
      "Serviço Social",
      "Pedagogia",
      "Terapia Ocupacional",
      "Fonoaudiologia",
      "Psicopedagogia"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.B > scores.E && scores.B > scores.A && scores.B > scores.G && scores.H >= 3
    }
  },
  {
    id: "comunicador-inspirador",
    name: "O Comunicador Inspirador",
    description: "Você tem talento para se expressar, conectar ideias e inspirar pessoas através da palavra e da criatividade.",
    characteristics: "comunicar ideias de forma envolvente, criar narrativas e inspirar mudanças",
    environment: "expressão criativa, comunicação estratégica e conexão com audiências",
    careers: [
      "Jornalismo",
      "Marketing",
      "Publicidade",
      "Locução",
      "Produção de Conteúdo",
      "Mídias Sociais",
      "Relações Públicas"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.H > scores.E && scores.H > scores.B && scores.H > scores.G && scores.A >= 2 && scores.A > scores.G
    }
  },
  {
    id: "analista-logico",
    name: "O Analista Lógico",
    description: "Você possui raciocínio estruturado e capacidade excepcional de resolver problemas complexos através da lógica e da técnica.",
    characteristics: "analisar sistemas, resolver problemas complexos e criar soluções técnicas",
    environment: "desafios intelectuais, estruturas lógicas e desenvolvimento de sistemas",
    careers: [
      "Engenharia (todas)",
      "Análise de Dados",
      "TI / Programação",
      "Arquitetura de Software",
      "Estatística",
      "Ciência da Computação",
      "Cibersegurança"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.E > scores.H && scores.E > scores.B && scores.E > scores.A && scores.E > scores.G && scores.C <= scores.A
    }
  },
  {
    id: "investigador-profundo",
    name: "O Investigador Profundo",
    description: "Você busca compreender os fenômenos em sua essência, combinando rigor científico com curiosidade intelectual.",
    characteristics: "investigar fenômenos, descobrir padrões e gerar conhecimento científico",
    environment: "pesquisa, experimentação e aprofundamento teórico",
    careers: [
      "Ciências Biológicas",
      "Biomedicina",
      "Pesquisa Laboratorial",
      "Química",
      "Física",
      "Matemática",
      "Perícia Criminal Científica"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.E > scores.H && scores.E > scores.B && scores.E > scores.A && scores.E > scores.G && scores.C > scores.A
    }
  },
  {
    id: "criador-artistico",
    name: "O Criador Artístico",
    description: "Você enxerga o mundo através da estética e tem o dom de transformar ideias em expressões visuais e sensoriais únicas.",
    characteristics: "criar experiências estéticas, expressar ideias visualmente e inovar em formas",
    environment: "liberdade criativa, experimentação artística e expressão pessoal",
    careers: [
      "Design Gráfico",
      "Moda",
      "Arquitetura",
      "Artes Visuais",
      "Cinema",
      "Fotografia",
      "Ilustração",
      "Game Design"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.A > scores.H && scores.A > scores.B && scores.A > scores.E && scores.A > scores.G && scores.E <= scores.B
    }
  },
  {
    id: "criador-estrutural",
    name: "O Criador Estrutural",
    description: "Você une criatividade e técnica, criando soluções que são tanto belas quanto funcionais.",
    characteristics: "projetar soluções que unem estética e funcionalidade",
    environment: "projetos que exigem visão técnica e sensibilidade estética",
    careers: [
      "Arquitetura",
      "Design de Produto",
      "Engenharia de Produção",
      "UX/UI",
      "Design de Mobiliário",
      "3D Design"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.A > scores.H && scores.A > scores.B && scores.A > scores.G && scores.E > scores.B && scores.E >= 3
    }
  },
  {
    id: "lider-processos",
    name: "O Líder de Processos",
    description: "Você tem visão sistêmica e capacidade de organizar recursos, pessoas e processos para alcançar objetivos.",
    characteristics: "coordenar equipes, otimizar processos e garantir resultados",
    environment: "gestão de projetos, liderança de equipes e estruturação de processos",
    careers: [
      "Administração",
      "Gestão de Projetos",
      "Gestão de Pessoas",
      "RH",
      "Logística",
      "Direito Empresarial"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.G > scores.A && scores.G > scores.B && scores.G > scores.E && scores.G > scores.H && scores.E <= scores.H
    }
  },
  {
    id: "empreendedor-nato",
    name: "O Empreendedor Nato",
    description: "Você possui visão de negócios, iniciativa e capacidade de transformar ideias em empreendimentos reais.",
    characteristics: "identificar oportunidades, criar negócios e liderar iniciativas",
    environment: "inovação, autonomia e construção de projetos próprios",
    careers: [
      "Administração",
      "Gestão Comercial",
      "Startups",
      "Negócios Digitais",
      "Marketing",
      "Vendas",
      "Produtos Digitais"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.G >= 4 && ((scores.B >= 2 && scores.A >= 2) || (scores.A >= 3 && scores.G >= scores.H))
    }
  },
  {
    id: "mediador-diplomatico",
    name: "O Mediador Diplomático",
    description: "Você tem habilidade para compreender diferentes perspectivas e facilitar diálogos construtivos.",
    characteristics: "mediar conflitos, facilitar acordos e promover entendimento mútuo",
    environment: "negociação, aconselhamento e mediação de relações",
    careers: [
      "Direito",
      "Mediação",
      "Coaching",
      "Recursos Humanos",
      "Mentoria",
      "Comunicação"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.H > scores.E && scores.H > scores.B && scores.H > scores.A && scores.G >= 2 && scores.G > scores.A
    }
  },
  {
    id: "educador-inspiracional",
    name: "O Educador Inspiracional",
    description: "Você tem o dom de ensinar, inspirar e facilitar processos de aprendizagem e desenvolvimento.",
    characteristics: "educar, inspirar aprendizagem e facilitar desenvolvimento pessoal",
    environment: "ensino, orientação e processos educacionais transformadores",
    careers: [
      "Pedagogia",
      "Psicopedagogia",
      "Letras",
      "Educação Física",
      "Ensino",
      "Orientação Educacional"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.H >= 4 && scores.B >= 3 && scores.H > scores.G && scores.H > scores.A
    }
  },
  {
    id: "cientista-social",
    name: "O Cientista Social",
    description: "Você combina análise rigorosa com compreensão de dinâmicas humanas e sociais.",
    characteristics: "analisar fenômenos sociais, compreender sistemas humanos e propor soluções",
    environment: "pesquisa social, análise de políticas e compreensão de dinâmicas coletivas",
    careers: [
      "Economia",
      "Sociologia",
      "Administração Pública",
      "Ciência Política",
      "Pesquisa de Opinião"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.H >= 3 && scores.E >= 3 && Math.abs(scores.H - scores.E) <= 2 && scores.H > scores.B
    }
  },
  {
    id: "tecnico-execucao",
    name: "O Técnico de Execução",
    description: "Você possui habilidades práticas e técnicas, com foco em resolver problemas concretos e executar soluções.",
    characteristics: "executar soluções técnicas, resolver problemas práticos e manter sistemas",
    environment: "execução técnica, manutenção de sistemas e aplicação prática",
    careers: [
      "Técnico em TI",
      "Análise de Suporte",
      "Técnico em Eletrônica",
      "Eletrotécnica",
      "Automação"
    ],
    matchRules: (scores: AdvancedScores) => {
      return scores.G > scores.A && scores.G > scores.B && scores.E >= 3 && scores.E > scores.H && Math.abs(scores.E - scores.G) <= 2
    }
  }
]

export const calculateMicroProfile = (scores: AdvancedScores): MicroProfile => {
  for (const profile of microProfiles) {
    if (profile.matchRules(scores)) {
      return profile
    }
  }

  const maxScore = Math.max(scores.H, scores.E, scores.B, scores.A, scores.G)

  if (scores.E === maxScore) return microProfiles[2]
  if (scores.H === maxScore) return microProfiles[1]
  if (scores.B === maxScore) return microProfiles[0]
  if (scores.A === maxScore) return microProfiles[4]
  if (scores.G === maxScore) return microProfiles[6]

  return microProfiles[2]
}

export const getPrimaryArea = (scores: AdvancedScores): string => {
  const maxScore = Math.max(scores.H, scores.E, scores.B, scores.A, scores.G)

  if (scores.H === maxScore) return "Humanas"
  if (scores.E === maxScore) return "Exatas"
  if (scores.B === maxScore) return "Biológicas"
  if (scores.A === maxScore) return "Artes e Criatividade"
  if (scores.G === maxScore) return "Gestão e Negócios"

  return "Multidisciplinar"
}
